import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Microscope, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';

interface SimpleResearchFormProps {
  onResearchGenerated?: () => void;
  onGenerateResearch?: (prompt: string) => Promise<void>;
}

export const SimpleResearchForm: React.FC<SimpleResearchFormProps> = ({
  onResearchGenerated,
  onGenerateResearch
}) => {
  const [researchPrompt, setResearchPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!researchPrompt.trim() || !onGenerateResearch) {
      return;
    }

    setIsGenerating(true);
    
    try {
      await onGenerateResearch(researchPrompt.trim());
      setResearchPrompt(''); // Reset form on success
      onResearchGenerated?.();
    } catch (error) {
      console.error('Research generation failed:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 w-full">
      {/* Description */}
      <p className="text-center text-gray-600 dark:text-gray-400 text-sm">
        Describe what you want to research and AI will provide the answers
      </p>
      
      {/* Research Prompt */}
      <div className="space-y-2">
        <Label 
          htmlFor="research-prompt" 
          className="text-sm font-medium"
        >
          Research Description
        </Label>
        <div className="relative">
          <Input
            id="research-prompt"
            value={researchPrompt}
            onChange={(e) => setResearchPrompt(e.target.value)}
            placeholder="e.g. AI trends in healthcare industry"
            className="w-full px-4 py-3 rounded-xl"
            disabled={isGenerating}
          />
        </div>
      </div>

      {/* Generate Button */}
      <motion.div
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <Button
          type="submit"
          disabled={!researchPrompt.trim() || isGenerating}
          className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-medium py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex items-center justify-center gap-3">
            {isGenerating ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Microscope className="h-5 w-5" />
            )}
            <span>
              {isGenerating ? 'Generating Research...' : 'Generate Research'}
            </span>
          </div>
        </Button>
      </motion.div>
    </form>
  );
}; 